// QR Kiosk v2 — sub-page scaffold: title row + children + floating BackPill.
// §7.1-A: the pill label is a function of the back TARGET, never a per-screen literal.
//   target = the machine dashboard ("menu")  → "Späť do menu" (kioskBackToMenu)
//   anything else (a declared parent route, navigate(-1), an overlay close) → "Späť" (kioskBack)
// Back targets are DECLARED per page (parent map in KV2_S7_EDITFLOW_DESIGN_SPEC.md §1.2),
// never discovered from a runtime history stack — deep-link + refresh safe. A fixed pill +
// an in-flow spacer so the last content is never stranded under it.
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Glyph } from "./glyphs";
import { CountPill } from "./StatusPip";
import { BackPill } from "./BackPill";
import { BACK_CLEARANCE, isMenuPath, menuPath } from "./kioskNav";

export interface KPageProps {
  titleKey?: string;
  glyph?: string;
  count?: number;
  /** Machine internalNumber — the menu back target (resolution order in §4.6). */
  machineNumber?: string;
  hideBack?: boolean;
  /** Explicit parent route (parent map §1.2). Omit → menu via machineNumber/store. */
  backTo?: string;
  /** Force "Späť" even while backTo is still unknown (loading a deep link). */
  backNested?: boolean;
  children?: React.ReactNode;
}

export function KPage({ titleKey, glyph, count, machineNumber, hideBack, backTo, backNested, children }: KPageProps) {
  const navigate = useNavigate();
  const { translate } = useTranslation("public");
  // Read-only fallback — harmless in KV2-0 (nothing populates it yet), valuable from KV2-1 on.
  const machineFromStore = useAppSelector((s) => s.public.machine?.internalNumber);

  const menuTarget = machineNumber || machineFromStore;
  const handleBack = () => {
    if (backTo) return navigate(backTo);
    if (backNested || !menuTarget) return navigate(-1);
    navigate(menuPath(menuTarget));
  };
  // "Späť do menu" ONLY when the resolved target IS the dashboard; else "Späť" (never flickers).
  const nested = backNested || (backTo ? !isMenuPath(backTo) : !menuTarget);

  return (
    <>
      {titleKey && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 16px 4px",
            flexShrink: 0,
          }}
        >
          {glyph && (
            <span style={{ color: "var(--muted)", display: "inline-flex" }}>
              <Glyph d={glyph} size={20} />
            </span>
          )}
          <span
            style={{ fontSize: 19, fontWeight: 700, letterSpacing: "-0.015em", color: "var(--ink)" }}
          >
            {translate(titleKey)}
          </span>
          {count != null && <CountPill n={count} />}
        </div>
      )}

      {children}

      {!hideBack && (
        <>
          {/* In-flow spacer — reserves scroll clearance under the fixed pill. */}
          <div aria-hidden="true" style={{ height: BACK_CLEARANCE, flexShrink: 0, marginTop: "auto" }} />
          <BackPill label={translate(nested ? "kioskBack" : "kioskBackToMenu")} onBack={handleBack} />
        </>
      )}
    </>
  );
}

export default KPage;
